import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";

/**
 * Some corporate networks (observed at Carrefour, Intersport and Leroy Merlin
 * HQs) block our microservice API domain `*.makesuccess.io` — most likely via
 * SNI / URL-category filtering that flags it as ad-tech — while leaving
 * `mobsuccess.com` and generic AWS domains reachable. Symptom: the dashboard
 * shell and every third-party script (ConfigCat, Sentry, AppSync) load fine,
 * but each data call dies with a bare `ERR_NETWORK` (no HTTP response) and the
 * user gets the misleading "you may have an adblocker" toast.
 *
 * The data API sits behind API Gateway, and the exact same endpoints are
 * reachable through the raw `<id>.execute-api.<region>.amazonaws.com/<stage>`
 * host, whose SNI is generic AWS and is not caught by ad-tech filters. CORS and
 * the Cognito authorizer are identical on both hosts (same API Gateway), so we
 * can reroute transparently.
 *
 * Strategy (two interceptors, attached to every axios instance):
 *  - request:  once a host is known-blocked for this session, rewrite its
 *              baseURL to the fallback BEFORE sending, so we skip the doomed
 *              request and its network timeout.
 *  - response: on the first `ERR_NETWORK` against a mapped host, flag it,
 *              report once, and retry on the fallback via the same instance. If
 *              the fallback also fails, the error propagates and the existing
 *              ConnectivityChecker toast fires.
 *
 * Loop safety comes from the map itself: the retry's baseURL is the execute-api
 * host, which is not a map key, so a second failure cannot trigger a third try.
 *
 * This is a tactical unblock. The permanent fix is to serve the API from a
 * `mobsuccess.com` subdomain so no ad-tech filter matches — see PR description.
 *
 * Environment gating is implicit: only the prod and master/dev hosts are listed,
 * so on `localhost` and PR stacks (which call execute-api URLs directly) both
 * interceptors no-op.
 */
const BLOCKED_HOST_FALLBACKS: Readonly<Record<string, string>> = {
  // production
  "dashboard-data.api.makesuccess.io":
    "https://a275ybbaea.execute-api.eu-central-1.amazonaws.com/v1",
  // master / dev account
  "dashboard-data.mobsuccess.dev.api.makesuccess.io":
    "https://uhxd1opqbg.execute-api.eu-central-1.amazonaws.com/v1",
};

const FLAG_PREFIX = "ms-blocked-domain-fallback:";

export type BlockedDomainFallbackInfo = {
  blockedHost: string;
  fallbackBaseURL: string;
};

let reporter: ((info: BlockedDomainFallbackInfo) => void) | null = null;

// In-memory source of truth for the current page lifetime — guarantees the
// fast path works even where `sessionStorage` is denied (some private-mode /
// sandboxed-iframe contexts throw on access). `sessionStorage` only adds
// persistence across reloads within the same tab.
const blockedHosts = new Set<string>();

/**
 * Register a reporter (e.g. Sentry `captureMessage`) invoked once per host, the
 * first time a blocked host is detected and rerouted this session. Pass `null`
 * to clear.
 */
export function setBlockedDomainFallbackReporter(
  fn: ((info: BlockedDomainFallbackInfo) => void) | null,
): void {
  reporter = fn;
}

function hostOf(url: string | undefined): string | null {
  if (!url) {
    return null;
  }
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

function isBlocked(host: string): boolean {
  if (blockedHosts.has(host)) {
    return true;
  }
  try {
    if (globalThis.sessionStorage?.getItem(`${FLAG_PREFIX}${host}`) === "1") {
      blockedHosts.add(host);
      return true;
    }
  } catch {
    // sessionStorage unavailable — in-memory Set is authoritative.
  }
  return false;
}

function markBlocked(host: string): void {
  blockedHosts.add(host);
  try {
    globalThis.sessionStorage?.setItem(`${FLAG_PREFIX}${host}`, "1");
  } catch {
    // sessionStorage unavailable — the in-memory Set still carries the flag for
    // this page lifetime.
  }
}

function isNetworkError(error: AxiosError): boolean {
  // A bare connection failure (blocked SNI, DNS, reset) surfaces as ERR_NETWORK
  // with no response. We deliberately do NOT reroute timeouts or HTTP errors:
  // those are backend problems, not domain blocks, and rerouting them would
  // double the load on a struggling backend.
  return error.code === "ERR_NETWORK" && !error.response;
}

/**
 * Request interceptor: if this baseURL's host is known-blocked this session,
 * rewrite it to the fallback before the request leaves the browser.
 */
export function blockedDomainRequestInterceptor(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  const host = hostOf(config.baseURL);
  if (host && BLOCKED_HOST_FALLBACKS[host] && isBlocked(host)) {
    config.baseURL = BLOCKED_HOST_FALLBACKS[host];
  }
  return config;
}

/**
 * Response error interceptor factory. On the first `ERR_NETWORK` against a
 * mapped host, flag it, report, and retry once on the fallback via `instance`.
 */
export function makeBlockedDomainResponseErrorInterceptor(
  instance: AxiosInstance,
): (error: AxiosError) => Promise<unknown> {
  return (error: AxiosError): Promise<unknown> => {
    const { config } = error;
    if (!config || !isNetworkError(error)) {
      return Promise.reject(error);
    }
    const host = hostOf(config.baseURL);
    const fallback = host ? BLOCKED_HOST_FALLBACKS[host] : undefined;
    if (!host || !fallback) {
      return Promise.reject(error);
    }

    if (!isBlocked(host)) {
      markBlocked(host);
      try {
        // Reporter (Sentry) is the observability signal — see the dashboard
        // bootstrap. Kept off `console` on purpose so the reroute stays silent.
        reporter?.({ blockedHost: host, fallbackBaseURL: fallback });
      } catch {
        // never let reporting break the retry
      }
    }

    // The retry targets the execute-api host, which is not a map key, so a
    // further failure rejects (and the ConnectivityChecker toast fires) rather
    // than looping.
    return instance.request({ ...config, baseURL: fallback });
  };
}

/** Test helper: clear the reporter and every session flag. */
export function _resetBlockedDomainFallbackState(): void {
  reporter = null;
  blockedHosts.clear();
  try {
    const ss = globalThis.sessionStorage;
    if (!ss) {
      return;
    }
    for (let i = ss.length - 1; i >= 0; i--) {
      const key = ss.key(i);
      if (key?.startsWith(FLAG_PREFIX)) {
        ss.removeItem(key);
      }
    }
  } catch {
    // ignore
  }
}
